import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import ProductCard from '../components/ProductCard';
import WhiteButtonMenu from '../utils/WhiteButtonMenu';
import { loadMenuDay, formatMenuDayLabel } from '../utils/menu';
import { loadBusinessHours, formatHourLabel } from '../utils/businessHours';


// El fetch del menú vive en utils/menu (loadMenuDay), que resuelve el día
// vigente y cachea la respuesta para todos los consumidores.

// Clasificación del menú por atributos (reemplaza las categorías por category_id).
// Orden fijo solicitado. Cada tab filtra por un campo del item:
//   - atributo_1: categoría base (Platillos, Caldos, ...)
//   - atributo_2: proteína (Pollo, Res, Cerdo, Pescado) — cruza categorías,
//     por eso un platillo de pollo aparece tanto en "Platillos" como en "Pollo".
const ATTRIBUTE_TABS = [
  // "Platillos" agrupa por category_id=1 (el platillo real del modelo de combos),
  // no por atributo_1: así incluye p.ej. ensaladas que son category_id=1 y coincide
  // con el conteo del admin (16), aunque su atributo_1 sea "Ensaladas".
  { label: 'Platillos',      field: 'category_id', value: 1 },
  { label: 'Pollo',          field: 'atributo_2', value: 'Pollo' },
  { label: 'Res',            field: 'atributo_2', value: 'Res' },
  { label: 'Cerdo',          field: 'atributo_2', value: 'Cerdo' },
  { label: 'Pescado',        field: 'atributo_2', value: 'Pescado' },
  { label: 'Caldos',         field: 'atributo_1', value: 'Caldos' },
  { label: 'Sopas y cremas', field: 'atributo_1', value: 'Sopas y cremas' },
  { label: 'Ensaladas',      field: 'atributo_1', value: 'Ensaladas' },
  { label: 'Guarniciones',   field: 'atributo_1', value: 'Guarniciones' },
  { label: 'Bebidas',        field: 'atributo_1', value: 'Bebidas' },
  { label: 'Postres',        field: 'atributo_1', value: 'Postres' },
];

// Normaliza para comparar (tolera espacios y mayúsculas en los datos).
const normAttr = (v) => (v == null ? '' : String(v).trim().toLowerCase());
const matchesTab = (product, tab) =>
  tab.field === 'category_id'
    ? Number(product.category_id) === Number(tab.value)
    : normAttr(product[tab.field]) === normAttr(tab.value);

const MenuSection = ({ reloadFlag, onArmarCombo, ordering = { open: true }, comboFromPrice = null }) => {
  // activeTab = value del tab activo (los values son únicos entre los 11).
  const [activeTab, setActiveTab] = useState(null);
  const [products, setProducts] = useState([]);
  const [menuDate, setMenuDate] = useState(null);
  // Only one product's size fan is open at a time (grid-friendly).
  const [openFanId, setOpenFanId] = useState(null);

  // La fecha del menú (hoy vs mañana según order_end configurado) viene de
  // utils/menu.getMenuDate + los horarios cargados de /settings/business-hours.

  // Add this function to format Google Drive URLs
  const formatGoogleDriveUrl = (url) => {
    if (!url) return '';

    // Extract the file ID from various Google Drive URL formats
    let fileId = '';

    // Format 1: https://drive.google.com/file/d/FILEID/view...
    const fileIdMatch = url.match(/\/d\/(.*?)(\/|$|\?)/);
    if (fileIdMatch) {
      fileId = fileIdMatch[1];
    }

    // Format 2: Already has the ID in uc?export=view format
    const ucMatch = url.match(/id=(.*?)($|&)/);
    if (ucMatch) {
      fileId = ucMatch[1];
    }

    if (!fileId) return url;

    // Use the direct download URL format
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
  };

  // 3. Cargar el menú vigente: hoy antes del corte, si no el siguiente día
  //    que tenga platillos (salta fines de semana y días cerrados).
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const businessHours = await loadBusinessHours();
        const menuDay = await loadMenuDay(businessHours);
        setMenuDate(menuDay.date);
        // Format the image URLs before setting the products
        setProducts(menuDay.items.map(product => ({
          ...product,
          img_item: formatGoogleDriveUrl(product.img_item)
        })));
      } catch (error) {
        console.error("Error al obtener productos:", error);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadFlag]);

  // 4. Solo mostramos los tabs (en el orden fijo) que tengan al menos un
  //    producto en el menú del día. Evita tabs vacíos.
  const visibleTabs = ATTRIBUTE_TABS.filter(tab => products.some(p => matchesTab(p, tab)));

  // 5. Al cargar/cambiar productos, fijar el tab activo al primero visible
  //    (o conservar el actual si sigue teniendo productos).
  useEffect(() => {
    const vis = ATTRIBUTE_TABS.filter(tab => products.some(p => matchesTab(p, tab)));
    setActiveTab(prev => (prev && vis.some(t => t.value === prev)) ? prev : (vis[0]?.value ?? null));
  }, [products]);

  // 6. Etiqueta del día y modo vitrina (menú visible, pedidos cerrados).
  const dayLabel = menuDate ? formatMenuDayLabel(menuDate) : '';
  const isShowcase = Boolean(menuDate) && ordering.open === false && ordering.opensAt;

  // 7. Filtrar productos por tab activo.
  const activeTabConfig = ATTRIBUTE_TABS.find(t => t.value === activeTab) || null;
  const filteredProducts = activeTabConfig
    ? products.filter(p => matchesTab(p, activeTabConfig))
    : [];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const categoryButtonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const productGridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2
      }
    }
  };

  const productCardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <section className="w-full py-6 sm:py-16 px-4 md:px-6">
      <div className="container mx-auto">
        {/* Título */}
        <motion.h2
          className="text-white text-2xl sm:text-4xl lg:text-5xl font-bold mb-4 text-left"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {menuDate ? `Menú del ${dayLabel}` : 'Descubre nuestro menú del día'}
        </motion.h2>

        {/* Subtítulo informativo */}
        <motion.p
          className="text-white/80 text-sm sm:text-base mb-4 text-left flex items-center gap-2"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Explora nuestros platillos con toda la información disponible
        </motion.p>

        {/* Modo vitrina: el menú se ve completo, pero los pedidos aún no abren */}
        {isShowcase && (
          <motion.div
            role="status"
            className="mb-8 flex items-start gap-3 rounded-2xl border border-amber-300/40 bg-amber-400/10 px-4 py-3 backdrop-blur-sm"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.3 }}
          >
            <svg className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M12 8v4l2.5 2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-amber-50 text-sm sm:text-base leading-snug">
              <span className="font-semibold">Este es el menú del {dayLabel.toLowerCase()}.</span>{' '}
              Puedes hacer tu pedido a partir de las {formatHourLabel(ordering.opensAt)} de ese día.
            </p>
          </motion.div>
        )}

        {/* Botones de categorías (atributos) — tira horizontal scrollable en todos los breakpoints */}
        <div className="sticky top-[96px] md:top-[128px] lg:top-[144px] z-10 left-0 right-0 -mx-4 md:-mx-6 bg-red-950/95 backdrop-blur-md mb-8 py-2 border-b border-white/10">
          <motion.div
            className="flex flex-nowrap overflow-x-auto gap-2 px-4 md:px-6 scrollbar-hide max-w-screen-xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            role="tablist"
            aria-label="Categorías del menú"
          >
            {visibleTabs.length > 0 ? (
              visibleTabs.map((tab) => (
                <motion.div
                  key={tab.value}
                  variants={categoryButtonVariants}
                  className="flex-shrink-0"
                >
                  <WhiteButtonMenu
                    text={tab.label}
                    isActive={activeTab === tab.value}
                    onClick={() => setActiveTab(tab.value)}
                  />
                </motion.div>
              ))
            ) : (
              <motion.p
                className="text-white py-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
              >
                Cargando menú...
              </motion.p>
            )}
          </motion.div>
        </div>

        {/* Grid de productos */}
        <AnimatePresence mode="wait">
          {filteredProducts.length > 0 ? (
            <motion.div
              key={activeTab}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-6 lg:gap-8 justify-items-center"
              variants={productGridVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {filteredProducts.map((item) => (
                <motion.div
                  key={item.id}
                  variants={productCardVariants}
                  whileHover={{
                    scale: 1.03,
                    transition: { duration: 0.2 }
                  }}
                  layout
                  className="w-full max-w-sm"
                >
                  <ProductCard
                    id={item.id}
                    image={item.img_item}
                    title={item.Nombre}
                    description={item.description || 'Sin descripción'}
                    price={item.price}
                    availability={item.product_counter || 0}
                    applyPromotions={item.apply_promotions}
                    presentations={item.presentations}
                    unitOfMeasure={item.unit_of_measure}
                    categoryId={item.category_id}
                    orderingOpen={ordering.open !== false}
                    comboFromPrice={comboFromPrice ? comboFromPrice(item.id) : null}
                    onArmarCombo={onArmarCombo}
                    fanOpen={openFanId === item.id}
                    onFanToggle={() => setOpenFanId((prev) => (prev === item.id ? null : item.id))}
                    onFanClose={() => setOpenFanId(null)}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.p
              key="no-products"
              className="text-white text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {menuDate
                ? `No hay productos de esta categoría en el menú del ${dayLabel.toLowerCase()}.`
                : 'No hay productos disponibles en esta categoría.'}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default MenuSection;
